import { useEffect, useMemo, useState } from "react";
import { Shield, Users, Sparkles, RefreshCw, Check, X, BarChart3, Camera, Trash2, UserPlus, AlertTriangle, MessageSquareQuote, Palette } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
    adminListUsers, adminGrantCommunity, adminRevokeCommunity, adminGrantPlus,
    adminDeleteUser, adminBulkDeleteUsers, adminCreateUser, formatApiError,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [selected, setSelected] = useState(new Set());
    const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);

    useEffect(() => {
        if (user && user !== false && user.role !== "admin") {
            toast.error("Admin access required");
            navigate("/");
        }
    }, [user, navigate]);

    const refresh = async () => {
        setLoading(true);
        try {
            const { users: list } = await adminListUsers();
            setUsers(list);
            setSelected(new Set());
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(); }, []);

    const filtered = useMemo(() => users.filter((u) => {
        if (!q) return true;
        const hay = `${u.email} ${u.name} ${u.community_org || ""}`.toLowerCase();
        return hay.includes(q.toLowerCase());
    }), [users, q]);

    const toggleOne = (id) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelected(next);
    };
    const toggleAll = () => {
        if (selected.size === filtered.length) setSelected(new Set());
        else setSelected(new Set(filtered.map((u) => u.id)));
    };

    const grantCommunity = async (email, org) => {
        try {
            await adminGrantCommunity({ email, community_org: org });
            toast.success(`${email} granted Community access`);
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const revokeCommunity = async (email) => {
        try {
            await adminRevokeCommunity({ email });
            toast.success(`${email} community access revoked`);
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const grantPlus = async (email, days = 30) => {
        try {
            await adminGrantPlus({ email, days });
            toast.success(`${email} granted ${days} days of Plus`);
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const deleteOne = async (u) => {
        if (!window.confirm(`Permanently delete ${u.email}?\nAll their gardens, beds, crops and photos will be removed.`)) return;
        try {
            const r = await adminDeleteUser(u.id);
            toast.success(`Deleted ${r.email} (${r.deleted.gardens} gardens, ${r.deleted.crops} crops)`);
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const doBulkDelete = async () => {
        try {
            const ids = [...selected];
            const r = await adminBulkDeleteUsers(ids);
            toast.success(`Deleted ${r.deleted} of ${r.requested} users`);
            const failed = r.results.filter((x) => !x.ok);
            if (failed.length > 0) {
                toast.message(`Skipped: ${failed.map((f) => `${f.user_id.slice(0, 6)}… (${f.reason})`).join(", ")}`);
            }
            setBulkConfirmOpen(false);
            refresh();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="space-y-8" data-testid="admin-page">
            <PageHeader
                overline="Admin"
                title="User management"
                description="Grant or revoke Community tier for verified projects, award Plus passes manually, create or delete accounts."
            />

            <section className="flex flex-wrap gap-3 items-center">
                <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search email or org…"
                    data-testid="admin-search-input"
                    className="max-w-md bg-[var(--vm-card)] border-[var(--vm-border)]"
                />
                <Button
                    type="button"
                    variant="outline"
                    onClick={refresh}
                    data-testid="admin-refresh-btn"
                    className="rounded-full border-[var(--vm-border)]"
                >
                    <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                </Button>
                <Button
                    type="button"
                    onClick={() => setCreateOpen(true)}
                    data-testid="admin-create-user-btn"
                    className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]"
                >
                    <UserPlus className="w-4 h-4 mr-2" /> Create user
                </Button>
                <Button
                    type="button"
                    onClick={() => selected.size > 0 ? setBulkConfirmOpen(true) : toast.message("Select users with the checkboxes first")}
                    data-testid="admin-bulk-delete-btn"
                    disabled={selected.size === 0}
                    variant="outline"
                    className="rounded-full border-[var(--vm-terracotta)] text-[var(--vm-terracotta)] disabled:opacity-40"
                >
                    <Trash2 className="w-4 h-4 mr-2" /> Delete selected ({selected.size})
                </Button>
                <Button
                    asChild
                    variant="outline"
                    data-testid="admin-stats-link"
                    className="rounded-full border-[var(--vm-accent)] text-[var(--vm-accent)] ml-auto"
                >
                    <Link to="/admin/stats"><BarChart3 className="w-4 h-4 mr-2" /> Platform stats</Link>
                </Button>
                <Button
                    asChild
                    variant="outline"
                    data-testid="admin-testimonials-link"
                    className="rounded-full border-[var(--vm-accent)] text-[var(--vm-accent)]"
                >
                    <Link to="/admin/testimonials"><MessageSquareQuote className="w-4 h-4 mr-2" /> Testimonials</Link>
                </Button>
                <Button
                    asChild
                    variant="outline"
                    data-testid="admin-illustrations-link"
                    className="rounded-full border-[var(--vm-accent)] text-[var(--vm-accent)]"
                >
                    <Link to="/admin/illustrations"><Palette className="w-4 h-4 mr-2" /> Illustrations</Link>
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    onClick={async () => {
                        toast.message("Seeding Pexels photos — about 50 API calls, ~30 seconds");
                        try {
                            const r = await axios.post(`${API}/admin/pexels/seed`, {}, { withCredentials: true });
                            toast.success(`Pexels: ${r.data.inserted} new, ${r.data.skipped} skipped, ${r.data.failed} failed`);
                        } catch (e) { toast.error(formatApiError(e)); }
                    }}
                    data-testid="admin-pexels-seed-btn"
                    className="rounded-full border-[var(--vm-sage)] text-[var(--vm-sage)]"
                >
                    <Camera className="w-4 h-4 mr-2" /> Seed Pexels photos
                </Button>
            </section>

            {loading ? (
                <p className="text-[var(--vm-text-muted)]">Loading…</p>
            ) : (
                <section className="journal-card overflow-x-auto" data-testid="admin-user-table">
                    <table className="w-full text-sm">
                        <thead className="bg-[var(--vm-input)] text-[var(--vm-text-muted)] uppercase text-xs tracking-wider">
                            <tr>
                                <th className="text-left px-3 py-3 w-10">
                                    <Checkbox
                                        checked={filtered.length > 0 && selected.size === filtered.length}
                                        onCheckedChange={toggleAll}
                                        data-testid="admin-select-all"
                                    />
                                </th>
                                <th className="text-left px-4 py-3">User</th>
                                <th className="text-left px-4 py-3">Tier</th>
                                <th className="text-left px-4 py-3">Community</th>
                                <th className="text-right px-4 py-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((u) => (
                                <UserRow
                                    key={u.id}
                                    u={u}
                                    selected={selected.has(u.id)}
                                    onToggle={() => toggleOne(u.id)}
                                    onGrantCommunity={grantCommunity}
                                    onRevokeCommunity={revokeCommunity}
                                    onGrantPlus={grantPlus}
                                    onDelete={deleteOne}
                                />
                            ))}
                        </tbody>
                    </table>
                </section>
            )}

            {/* Bulk delete confirm */}
            <Dialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
                <DialogContent data-testid="bulk-delete-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-[var(--vm-terracotta)]" /> Delete {selected.size} users?
                        </DialogTitle>
                        <DialogDescription>
                            This removes each user's account, gardens, beds, crops, photos, notes, and chat history.
                            Payment records are kept but anonymised for audit. The last remaining admin and your own
                            account are automatically skipped.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setBulkConfirmOpen(false)} data-testid="bulk-delete-cancel">Cancel</Button>
                        <Button
                            onClick={doBulkDelete}
                            data-testid="bulk-delete-confirm"
                            className="bg-[var(--vm-terracotta)] hover:opacity-90 text-[var(--vm-bg)]"
                        >
                            <Trash2 className="w-4 h-4 mr-2" /> Delete {selected.size} users
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Create user */}
            <CreateUserDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={refresh} />
        </div>
    );
}

function UserRow({ u, selected, onToggle, onGrantCommunity, onRevokeCommunity, onGrantPlus, onDelete }) {
    const [org, setOrg] = useState(u.community_org || "");
    const [expanded, setExpanded] = useState(false);

    return (
        <>
            <tr data-testid={`admin-user-row-${u.email}`} className="border-t border-[var(--vm-border)]">
                <td className="px-3 py-3">
                    <Checkbox
                        checked={selected}
                        onCheckedChange={onToggle}
                        data-testid={`admin-select-${u.email}`}
                    />
                </td>
                <td className="px-4 py-3">
                    <div className="font-medium text-[var(--vm-text)]">{u.name}</div>
                    <div className="text-xs text-[var(--vm-text-muted)]">{u.email}</div>
                </td>
                <td className="px-4 py-3">
                    <TierBadge tier={u.tier} />
                </td>
                <td className="px-4 py-3 text-[var(--vm-text-muted)]">
                    {u.is_community ? (
                        <span className="inline-flex items-center gap-1 text-[var(--vm-sage)]"><Check className="w-3 h-3" />{u.community_org || "verified"}</span>
                    ) : "—"}
                </td>
                <td className="px-4 py-3 text-right space-x-1">
                    <Button
                        type="button" size="sm" variant="outline"
                        onClick={() => setExpanded(!expanded)}
                        data-testid={`admin-toggle-${u.email}`}
                        className="rounded-full border-[var(--vm-border)]"
                    >
                        {expanded ? "Close" : "Manage"}
                    </Button>
                    <Button
                        type="button" size="sm" variant="outline"
                        onClick={() => onDelete(u)}
                        data-testid={`admin-delete-${u.email}`}
                        className="rounded-full border-[var(--vm-terracotta)] text-[var(--vm-terracotta)]"
                    >
                        <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                </td>
            </tr>
            {expanded && (
                <tr data-testid={`admin-user-actions-${u.email}`} className="bg-[var(--vm-input)]/40">
                    <td colSpan={5} className="px-4 py-3 space-y-3">
                        <div className="flex gap-2 items-center flex-wrap">
                            <Users className="w-4 h-4 text-[var(--vm-sage)]" />
                            <Input
                                value={org}
                                onChange={(e) => setOrg(e.target.value)}
                                placeholder="Organisation / project name"
                                data-testid={`admin-org-input-${u.email}`}
                                className="max-w-md bg-[var(--vm-card)] border-[var(--vm-border)]"
                            />
                            {u.is_community ? (
                                <Button
                                    type="button" size="sm"
                                    onClick={() => onRevokeCommunity(u.email)}
                                    data-testid={`admin-revoke-community-${u.email}`}
                                    variant="outline"
                                    className="rounded-full border-[var(--vm-terracotta)] text-[var(--vm-terracotta)]"
                                >
                                    <X className="w-4 h-4 mr-1" /> Revoke community
                                </Button>
                            ) : (
                                <Button
                                    type="button" size="sm"
                                    onClick={() => onGrantCommunity(u.email, org)}
                                    data-testid={`admin-grant-community-${u.email}`}
                                    className="rounded-full bg-[var(--vm-sage)] hover:opacity-90 text-[var(--vm-bg)]"
                                >
                                    <Users className="w-4 h-4 mr-1" /> Grant community
                                </Button>
                            )}
                        </div>
                        <div className="flex gap-2 items-center flex-wrap">
                            <Sparkles className="w-4 h-4 text-[var(--vm-accent)]" />
                            <span className="text-sm text-[var(--vm-text-muted)]">Grant Plus pass for:</span>
                            {[7, 30, 90, 365].map((d) => (
                                <Button
                                    key={d}
                                    type="button" size="sm" variant="outline"
                                    onClick={() => onGrantPlus(u.email, d)}
                                    data-testid={`admin-grant-plus-${u.email}-${d}`}
                                    className="rounded-full border-[var(--vm-accent)] text-[var(--vm-accent)]"
                                >
                                    {d} days
                                </Button>
                            ))}
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}

function CreateUserDialog({ open, onOpenChange, onCreated }) {
    const [form, setForm] = useState({
        email: "", password: "", name: "",
        role: "user", is_community: false, community_org: "", plus_days: 0,
    });
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (!form.email || !form.password || !form.name) {
            toast.error("Email, password and name are required.");
            return;
        }
        if (form.password.length < 8) {
            toast.error("Password must be at least 8 characters.");
            return;
        }
        setBusy(true);
        try {
            await adminCreateUser(form);
            toast.success(`Created ${form.email}`);
            onCreated();
            onOpenChange(false);
            setForm({ email: "", password: "", name: "", role: "user", is_community: false, community_org: "", plus_days: 0 });
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="create-user-dialog">
                <DialogHeader>
                    <DialogTitle>Create a new user</DialogTitle>
                    <DialogDescription>
                        Admin-only — creates the account directly without the self-register flow.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label>Email</Label>
                        <Input
                            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                            type="email" data-testid="create-user-email" autoComplete="off"
                        />
                    </div>
                    <div>
                        <Label>Full name</Label>
                        <Input
                            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                            data-testid="create-user-name"
                        />
                    </div>
                    <div>
                        <Label>Temporary password (min 8 chars)</Label>
                        <Input
                            value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                            type="text" data-testid="create-user-password"
                        />
                    </div>
                    <div className="flex gap-3 items-center">
                        <Label>Role</Label>
                        <select
                            value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                            data-testid="create-user-role"
                            className="bg-[var(--vm-card)] border border-[var(--vm-border)] rounded-md px-3 py-1.5 text-sm"
                        >
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                        </select>
                        <Label className="ml-4">
                            <Checkbox
                                checked={form.is_community}
                                onCheckedChange={(v) => setForm({ ...form, is_community: !!v })}
                                data-testid="create-user-community"
                                className="mr-2"
                            />
                            Community tier
                        </Label>
                    </div>
                    {form.is_community && (
                        <div>
                            <Label>Community organisation</Label>
                            <Input
                                value={form.community_org}
                                onChange={(e) => setForm({ ...form, community_org: e.target.value })}
                                data-testid="create-user-org"
                            />
                        </div>
                    )}
                    <div>
                        <Label>Grant Plus pass for</Label>
                        <select
                            value={form.plus_days} onChange={(e) => setForm({ ...form, plus_days: Number(e.target.value) })}
                            data-testid="create-user-plus-days"
                            className="bg-[var(--vm-card)] border border-[var(--vm-border)] rounded-md px-3 py-1.5 text-sm w-full"
                        >
                            <option value={0}>No Plus pass</option>
                            <option value={7}>7 days</option>
                            <option value={30}>30 days</option>
                            <option value={90}>90 days</option>
                            <option value={365}>1 year</option>
                        </select>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button
                        onClick={submit} disabled={busy}
                        data-testid="create-user-submit"
                        className="bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]"
                    >
                        <UserPlus className="w-4 h-4 mr-2" /> Create
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function TierBadge({ tier }) {
    const map = {
        plus:      { text: "Plus",      bg: "bg-[var(--vm-accent)]/15",   fg: "text-[var(--vm-accent)]",   border: "border-[var(--vm-accent)]/40" },
        community: { text: "Community", bg: "bg-[var(--vm-sage)]/15",     fg: "text-[var(--vm-sage)]",     border: "border-[var(--vm-sage)]/40" },
        free:      { text: "Free",      bg: "bg-[var(--vm-input)]",       fg: "text-[var(--vm-text-muted)]", border: "border-[var(--vm-border)]" },
    };
    const s = map[tier] || map.free;
    return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${s.bg} ${s.fg} ${s.border}`}>{s.text}</span>;
}
